// src/pages/PrivacyPolicy.jsx
import React, { useState, useEffect } from "react"; // Add useState and useEffect
import { useNavigate } from "react-router-dom";
import { Link } from 'react-router-dom';
import './LandingPage.css'; // Import landing page styles for navbar, footer, background
import './PrivacyPolicy.css'; // Import privacy-specific styles

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false); // Add mobile menu state
  const [isScrolled, setIsScrolled] = useState(false); // Add scroll state

  // Add scroll listener
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="container">
      <div className="background-overlay"></div>
      
      {/* Same navbar as landing page with floating effect */}
      <div className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
        <div className="logo-container">
          <Link to="/">
            <img src="/ob_logo_small_white.png" alt="OffsetBridge logo" className="logo-img" />
          </Link>
          <Link to="/" className="logo-text">OffsetBridge</Link>
        </div>
        
        {/* Desktop Navigation */}
        <div className="nav-buttons desktop-nav">
          <button className="howItWorks-btn" onClick={() => navigate("/how-it-works")}>
            How It Works
          </button>
          <button className="login-btn" onClick={() => navigate("/login")}>
            Log In
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button 
          className="mobile-menu-btn" 
          onClick={() => setMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span className={`hamburger ${isMobileMenuOpen ? 'open' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>
      </div>

      {/* Mobile Menu Dropdown */}
      <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
        <button 
          className="mobile-menu-item" 
          onClick={() => {
            navigate("/how-it-works");
            setMobileMenuOpen(false);
          }}
        >
          How It Works
        </button>
        <button 
          className="mobile-menu-item" 
          onClick={() => {
            navigate("/login");
            setMobileMenuOpen(false);
          }}
        >
          Log In
        </button>
      </div>

      {/* Privacy Policy content */}
      <div className="privacy-content">
        <div className="privacy-card">
          <h1 className="privacy-title">Privacy Policy</h1>
          
          <div className="privacy-section">
            <p>
              Last revised: November 1, 2025
            </p>
            <p>
            Your privacy matters to us. This Privacy Policy describes how your personal information is collected, used, 
            and shared when you use our platform (the “Platform”) or when you contact us by email or other means of 
            communication. By accessing or using the Platform, you acknowledge that you have read, 
            understood, and agreed to the terms set forth in this Privacy Policy.
            </p>
          </div>
          <div className="privacy-section">
            <h2 className="privacy-heading">1. Contact</h2>
            <p>
            For all questions, requests, or complaints relating to the processing of personal data, you may contact:
            <br></br>
            <br></br>OffsetBridge
            <br></br>Bremgartnerstrasse 77
            <br></br>CH-8003 Zürich CH 
            <br></br>
            <br></br>Email address: <a href="mailto:team@offsetbridge.com" className="privacy-email">team@offsetbridge.com</a>
            </p>
          </div>
          <div className="privacy-section">
            <h2 className="privacy-heading">2. Personal data we collect</h2>
            <p>
            We collect two main types of data :
            <ul className="privacy-list">
              <li>Account Information: Data you provide when creating or accessing an account, such as your email address and login credentials.</li>
              <li>Usage & Technical Data: Information automatically collected when you use the Platform, which may include anonymized analytics, feature usage patterns, and technical details (e.g., browser type, device, IP address).</li>
            </ul>
            </p>
            <p>
            In addition, depending on your interactions with us, we may also collect:
            <ul className="privacy-list">
              <li>Contact information (such as name, address, and phone number, if voluntarily provided);</li>
              <li>Data relating to your preferences and communication history (emails, messages, or feedback shared with our team);</li>
              <li>Any other information you voluntarily provide during support or communication with us.</li>
            </ul>
            </p>
          </div>

          <div className="privacy-section">
            <h2 className="privacy-heading">3. Use of personal data</h2>
            <p>
              We use the data we collect to:
            </p>
            <ul className="privacy-list">
              <li>Manage your user account and authenticate logins;</li>
              <li>Improve the performance, design and functionality of the Platform;</li>
              <li>Conduct research, analytics, and understand market needs;</li>
              <li>Communicate with you regarding updates, security notices, or support issues.</li>
            </ul>
            <p>
            Additionally, your personal data may be processed when necessary to comply with legal obligations or 
            protect our legitimate interests, such as ensuring the security and proper functioning of our systems. 
            If we intend to use your personal data for purposes other than those stated above, we will inform you 
            and, where required, obtain your consent.
            </p>
          </div>

          <div className="privacy-section">
            <h2 className="privacy-heading">4. Data sharing</h2>
            <p>
            We do not sell or rent your personal data to third parties.
            However, we may share certain data under the following circumstances:
            <ul className="privacy-list">
              <li>With trusted third-party service providers (e.g., IT hosting, analytics, or customer support) who process data on our behalf under confidentiality obligations;</li>
              <li>With authorities or legal entities when required by law or regulation;</li>
              <li>With research or analytics partners, but only in aggregated or anonymized form.</li>
            </ul>
            We ensure that any third party processing your data provides adequate data protection safeguards.
            </p>
          </div>

          <div className="privacy-section">
            <h2 className="privacy-heading">5. Data security</h2>
            <p>
            We take reasonable, technical and organizational measures to protect your personal information 
            from unauthorized access, loss, misuse, or alteration.
            Despite these efforts, please note that no data transmission or storage system can be guaranteed 
            to be 100% secure.
            </p>
          </div>
          <div className="privacy-section">
            <h2 className="privacy-heading">6. Data retention</h2>
            <p>
            We retain personal data only as long as necessary for the purposes described above or 
            as required by applicable law. Once the retention period expires, your data will be securely 
            deleted or anonymized.
            </p>
          </div>
          <div className="privacy-section">
            <h2 className="privacy-heading">7. Your rights</h2>
            <p>
            You have the following rights under applicable data protection laws:
            <ul className="privacy-list">
              <li>Right of access: you can obtain information relating to the processing of your personal data, and a copy of such personal data;</li>
              <li>Right to inspect: you have the right to request confirmation if personal data is being processed;</li>
              <li>Right to rectification: where you consider that your personal data is inaccurate or incomplete, you can require that such personal data be modified accordingly;</li>
              <li>Right of erasure: you can require the deletion of your personal data, to the extent permitted by law;</li>
              <li>Right of restriction of processing: you can request the restriction of the processing of your personal data;</li>
              <li>Right to object to the processing: you can object to the processing of your personal data, on grounds relating to your particular situation;</li>
              <li>Right to revoke the consent: where you have given your consent for the processing of your personal data, you have the right to withdraw your consent at any time;</li>
              <li>Right to data portability: where legally applicable, you have the right to have the personal data you have provided to us be returned to you or, where technically feasible, transferred to a third party;</li>
              <li>Right to lodge a complaint: see above.</li>
            </ul>
            Please note that exercising some of these rights may limit or prevent access to certain Platform features.
            </p>
          </div>
          <div className="privacy-section">
            <h2 className="privacy-heading">8. Changes to this privacy policy</h2>
            <p>
            We may revise this Privacy Policy from time to time. Material changes will be communicated via the Platform or by email. 
            We encourage you to review this Policy periodically to stay informed about how we protect your data.
            </p>
          </div>
          <div className="privacy-section">
            <h2 className="privacy-heading">9. Hosting and data location</h2>
            <p>
            Our Platform and related services may be hosted by trusted third-party providers offering secure and reliable infrastructure. 
            Information on hosting location and providers can be obtained upon request.
            </p>
          </div>

        </div>
      </div>

      {/* Same footer as landing page */}
      <div className="footer">
        © 2025 OffsetBridge
        <Link to="/privacy-policy" className="privacy-link">
          Privacy Policy
        </Link>
        <Link to="/terms-and-conditions" className="terms-link">
          Terms & Conditions
        </Link>
      </div>
    </div>
  );
}